/* HTML game: loads the embed on click (when lazy) and wires the Fullscreen API on the stage wrapper. */
class GameEmbed extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.stage = this.querySelector('[data-game-stage]');
    this.frame = this.querySelector('[data-game-frame]');
    this.poster = this.querySelector('[data-game-play]');
    this.tpl = this.querySelector('[data-game-template]');
    this.fsBtn = this.querySelector('[data-game-fullscreen]');

    if (this.poster && this.tpl) this.poster.addEventListener('click', () => this.load());

    if (this.fsBtn && this.stage) {
      if (this.stage.requestFullscreen || this.stage.webkitRequestFullscreen) {
        this.fsBtn.hidden = false;
        this.fsBtn.addEventListener('click', () => this.toggleFullscreen());
        document.addEventListener('fullscreenchange', () => this.syncLabel());
        document.addEventListener('webkitfullscreenchange', () => this.syncLabel());
      }
    }
  }

  load() {
    if (!this.tpl) return;
    const holder = document.createElement('div');
    holder.innerHTML = this.tpl.innerHTML;
    const scripts = [...holder.querySelectorAll('script')];
    this.poster.hidden = true;
    this.frame.tabIndex = -1;
    while (holder.firstChild) this.frame.appendChild(holder.firstChild);
    this.tpl.remove();
    this.tpl = null;
    this.frame.focus();
    // innerHTML does not run scripts: re-insert them one at a time, in order, so custom embed code works.
    this.runScripts(scripts);
  }

  runScripts(scripts) {
    const frame = this.frame;
    // Scripts injected after page load cannot use document.write; route it into the game frame instead.
    const write = document.write;
    const writeln = document.writeln;
    const put = (...html) => frame.insertAdjacentHTML('beforeend', html.join(''));
    document.write = put;
    document.writeln = (...html) => put(...html, '\n');
    const finish = () => {
      document.write = write;
      document.writeln = writeln;
    };
    const next = (i) => {
      if (i >= scripts.length) return finish();
      const old = scripts[i];
      const fresh = document.createElement('script');
      [...old.attributes].forEach((a) => fresh.setAttribute(a.name, a.value));
      fresh.text = old.text;
      if (old.src) {
        fresh.async = false;
        fresh.onload = fresh.onerror = () => next(i + 1);
        old.replaceWith(fresh);
      } else {
        old.replaceWith(fresh);
        next(i + 1);
      }
    };
    next(0);
  }

  fsElement() {
    return document.fullscreenElement || document.webkitFullscreenElement;
  }

  toggleFullscreen() {
    const el = this.stage;
    if (this.fsElement() === el) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) Promise.resolve(exit.call(document)).catch(() => {});
    } else {
      if (this.tpl) this.load();
      const req = el.requestFullscreen || el.webkitRequestFullscreen;
      Promise.resolve(req.call(el)).catch(() => {});
    }
  }

  syncLabel() {
    this.fsBtn.textContent = this.fsElement() === this.stage ? 'Exit fullscreen' : 'Fullscreen';
  }
}
if (!customElements.get('game-embed')) customElements.define('game-embed', GameEmbed);

/* Game tabs: neighbouring "HTML game" sections with "Show as tab" on are grouped into one tabbed block.
   Sections stay in place (theme-editor safe); tabs only toggle which one is visible. */
(function () {
  if (window.__gmTabs) return;
  window.__gmTabs = true;
  const active = {}; // lead section id -> active section id
  const sectionOf = (gm) => gm.closest('.shopify-section') || gm.parentElement;

  function reset() {
    document.querySelectorAll('.gm-tabs').forEach((t) => t.remove());
    document.querySelectorAll('.gm--grouped').forEach((gm) => {
      gm.classList.remove('gm--grouped', 'gm--lead', 'gm--follow', 'is-active');
      gm.style.removeProperty('--sec-bg');
      gm.style.removeProperty('--sec-ink');
      gm.style.removeProperty('--pb');
      const sec = sectionOf(gm);
      if (sec) sec.hidden = false;
    });
  }

  function groups() {
    const out = [];
    let cur = null;
    let prevSec = null;
    document.querySelectorAll('.gm[data-tabs="true"]').forEach((gm) => {
      const sec = sectionOf(gm);
      if (cur && prevSec && sec.previousElementSibling === prevSec) cur.push(gm);
      else { cur = [gm]; out.push(cur); }
      prevSec = sec;
    });
    return out.filter((g) => g.length > 1);
  }

  function show(group, idx) {
    const lead = group[0];
    active[lead.dataset.sectionId] = group[idx].dataset.sectionId;
    group.forEach((gm, i) => {
      const on = i === idx;
      gm.classList.toggle('is-active', on);
      if (i > 0) sectionOf(gm).hidden = !on;
    });
    lead.querySelectorAll('.gm-tabs__tab').forEach((b, i) => {
      b.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      b.tabIndex = i === idx ? 0 : -1;
    });
  }

  function build() {
    reset();
    groups().forEach((group) => {
      const lead = group[0];
      const bg = lead.style.getPropertyValue('--sec-bg');
      const ink = lead.style.getPropertyValue('--sec-ink');
      const pb = lead.style.getPropertyValue('--pb');
      group.forEach((gm, i) => {
        gm.classList.add('gm--grouped', i === 0 ? 'gm--lead' : 'gm--follow');
        if (i > 0) {
          gm.style.setProperty('--sec-bg', bg);
          gm.style.setProperty('--sec-ink', ink);
          gm.style.setProperty('--pb', pb);
        }
      });
      const bar = document.createElement('div');
      bar.className = 'gm-tabs';
      bar.setAttribute('role', 'tablist');
      bar.setAttribute('aria-label', 'Games');
      group.forEach((gm, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'gm-tabs__tab';
        b.setAttribute('role', 'tab');
        b.textContent = gm.dataset.tabLabel || 'Game ' + (i + 1);
        b.addEventListener('click', () => show(group, i));
        b.addEventListener('keydown', (e) => {
          const n = group.length;
          let j = null;
          if (e.key === 'ArrowRight') j = (i + 1) % n;
          if (e.key === 'ArrowLeft') j = (i - 1 + n) % n;
          if (j === null) return;
          e.preventDefault();
          show(group, j);
          bar.children[j].focus();
        });
        bar.appendChild(b);
      });
      const anchor = lead.querySelector('.gm__head');
      const holder = lead.querySelector('.page-width') || lead;
      if (anchor) anchor.after(bar); else holder.prepend(bar);
      const want = active[lead.dataset.sectionId];
      const idx = Math.max(0, group.findIndex((g) => g.dataset.sectionId === want));
      show(group, idx);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build);
  else build();

  if (window.Shopify && Shopify.designMode) {
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((ev) =>
      document.addEventListener(ev, () => setTimeout(build, 0))
    );
    document.addEventListener('shopify:section:select', (e) => {
      setTimeout(() => {
        const gm = e.target.querySelector && e.target.querySelector('.gm.gm--grouped');
        if (!gm) return;
        groups().forEach((group) => {
          const i = group.indexOf(gm);
          if (i > -1) show(group, i);
        });
      }, 0);
    });
  }
})();